import React, { useState, useEffect } from 'react';
import { navigate } from '@reach/router';
import {
  Stack,
  InputWithAdornment,
  SearchIcon,
  MenuItem,
  List,
  ListItem,
  Link,
  Element,
  SearchInput,
  merge
} from '@quotient/riq-react-ui';
import { getUrlParameter, generateSignInLink } from '@quotient/riq-helpers';
import { AppContext } from '@quotient/riq-state';
import { ShoppingListComp } from './ShoppingListComp';

interface ICarrierProps {
  showSortBy?: boolean;
  showCategory?: boolean;
  onSearchSubmit?: (q: string) => void;
  ref?: React.Ref<unknown>;
}

// @TODO: Add cartet inverted as an icon
const styles = {
  caretInverted: {
    position: 'absolute',
    height: 0,
    width: 0,
    content: '""',
    zIndex: 11,
    top: '-10px',
    left: '50px',
    borderRight: '10px solid transparent',
    borderBottom: '10px solid #fff',
    borderLeft: '10px solid transparent'
  }
};

const generateList = (items: any) => {
  let result: any[] = [];

  if (Array.isArray(items)) {
    items.forEach((item, i) => {
      if (Array.isArray(item)) {
        return result.push(
          <List marginBottom={4} key={i}>
            {generateList(item)}
          </List>
        );
      }
      result.push(
        <ListItem key={item.name} isActive={item.active === 'active'}>
          <MenuItem>
            <Link variant="primary" href={item.url}>
              {item.name} ({item.count})
            </Link>
          </MenuItem>
        </ListItem>
      );
    });
  }

  return result;
};

const Carrier: React.FC<ICarrierProps> = React.forwardRef(function Carrier({ onSearchSubmit, ...props }, ref) {
  const query = getUrlParameter('q');

  const [q, setQ] = useState(query || '');
  const { appState, setAppState }: any = React.useContext(AppContext);
  const { isUserLoggedIn, signinlink } = appState?.getCommonData?.CI_SigninSignupLinks || {};
  const [searchContent, setSearchContent] = React.useState(false);
  const [isSticky, setSticky] = React.useState(false);

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);
    return () => {
      // Remove scroll event after the component is unmount
      document.removeEventListener('scroll', () => handleScroll);
    };
  }, []);

  const handleScroll = (e: any) => {
    if (e.target.scrollingElement.scrollTop >= 125) {
      setSticky(true);
    } else {
      setSticky(false);
    }
  };

  const onInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    if (e.currentTarget.value.length > 0) {
      setSearchContent(true);
    } else {
      setSearchContent(false);
    }
    setQ(e.currentTarget.value);
  };

  const changeSearchContent = (value: boolean) => {
    setSearchContent(value);
  };

  const checkOnKey = (e: React.KeyboardEvent) => {
    if (e.keyCode === 13) updateSearchQuery(q);
    else if (e.keyCode === 27) updateSearchQuery('');
  };

  const checkOnClick = (e: any) => {
    updateSearchQuery(q);
  };

  const updateSearchQuery = (q: string) => {
    if (q === '') {
      setQ(q);
      setSearchContent(false);
      return;
    }
    if (query === q) return;
    navigate('/search?q=' + q);
  };

  useEffect(() => {
    setAppState({ isCarrierMenuOpen: true });
  }, []);

  const list = appState?.getCommonData?.offcanvasShoppingList;
  const shoppinglistData = {
    listId: list?.listId || '',
    listIdentifier: list?.listIdentifier || '',
    showListIdentifier: list?.listItems?.showListIdentifier || true,
    sortable: list?.listItems?.sortable || false,
    sortfilter_data: list?.listItems?.sortfilter_data || [],
    sortfilter: list?.listItems?.sortfilter || '',
    arrItems: list?.listItems?.arrItems || [],
    isUserLoggedIn,
    signinlink: generateSignInLink('/')
  };

  return (
    <Stack
      ref={ref}
      marginY={2}
      align="center"
      justify="flex-end"
      css={merge(
        isSticky ? { position: 'fixed', zIndex: 2, top: ['45px', '45px', '45px', '-8px'], width: '1170px' } : {},
        {
          backgroundColor: 'grays.200',
          padding: 2,
          height: ['44px', 'auto'],
          paddingRight: [0, 8]
        }
      )}
      {...props}
    >
      <Stack
        align="center"
        css={
          isSticky ? { position: ['fixed', 'fixed', 'fixed', 'initial'], zIndex: 2, top: '55.5px', right: '45px' } : {}
        }
      >
        <InputWithAdornment
          css={{ display: ['none', 'flex'] }}
          size="large"
          type="search"
          after={<SearchIcon fill="white" />}
          placeholder="Search here..."
          value={q}
          onSubmit={checkOnClick}
          onKeyDown={checkOnKey}
          onChange={onInputChange}
        />
        <SearchInput
          css={merge(
            {
              display: ['flex', 'none'],
              position: 'absolute',
              width: '100%',
              height: '44px',
              top: '110px',
              left: 0,
              justifyContent: 'flex-end'
            },
            isSticky ? { position: 'fixed', zIndex: 2, top: '53.5px', left: '0', width: '97%' } : {}
          )}
          placeholder="Search here..."
          value={q}
          searchContent={searchContent}
          updateSearchContent={changeSearchContent}
          onSubmit={checkOnClick}
          onKeyDown={checkOnKey}
          onChange={onInputChange}
          updateValue={updateSearchQuery}
          buttonTextColor="white"
        />
        <ShoppingListComp data={shoppinglistData} />
      </Stack>
    </Stack>
  );
});

export default Carrier;
